import { useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'

import { Button, DrawerBodyWrapper, DrawerFooterWrapper, Input, Select, TOAST_TYPE } from '@base'
import { CountryCodes, INSPECTION_USER_DESIGNATIONS, ORG_USER_ROLES, strings } from '@constants'
import { BiFieldWrapper, FieldWrapper } from '../../../../OrgsAndUsers/Organisation/styles'
import { addUser, getUsersOfOrganisation } from '@data'
import { showLoader, showToast } from '@data/state/action'
import { DrawerButtonsWrapper, InputWrapper, NoteWrapper } from '../../../../OrgsAndUsers/Organisation/OrgProbe/styles'
import { useForceUpdate } from '@hooks'

const defaultState = {
	role: {
		value: ORG_USER_ROLES.ORG_ADMIN,
		valid: true,
	},
	full_name: {
		id: 'full-name',
		label: strings('full', 'name'),
		placeholder: strings(['enter', 'full', 'name']),
		value: '',
		valid: true,
		required: true,
	},
	email_id: {
		id: 'email-id',
		label: strings('email', 'id'),
		placeholder: strings(['enter', 'email', 'id']),
		value: '',
		valid: true,
	},
	mobile_number: {
		id: 'mobile-number',
		label: strings('mobile', 'number'),
		placeholder: strings(['enter', 'mobile', 'number']),
		value: '',
		valid: true,
		required: true,
	},
	designation: {
		id: 'designation',
		label: strings('designation'),
		placeholder: strings('select', 'designation'),
		value: '',
		required: true,
		disabled: false,
	},
}

const designationTypes = Object.entries(INSPECTION_USER_DESIGNATIONS).map(([key, value]) => ({ key, label: value }))

const AddUserInspection = ({ orgId, addCallback }) => {
	const dispatch = useDispatch()
	const [details, setDetails] = useState(defaultState)
	const [userList, setUserList] = useState([])
	const [selectedUser, setSelectedUser] = useState()
	const [showForm, setShowForm] = useState(false)
	const [countryCode, setCountryCode] = useState(CountryCodes[0])
	const loggedInUser = useSelector((state) => state.getIn(['user', 'user']))
	const [forceUpdate, update] = useForceUpdate()

	useEffect(() => {
		dispatch(showLoader(true))
		getUsersOfOrganisation(orgId)
			.then((response) => {
				setUserList(response)
			})
			.catch((error) => {
				let error_msg = error?.response?.data?.message ?? strings('msg_failed_to_fetch')
				dispatch(showToast(true, error_msg, { type: TOAST_TYPE.ERROR }))
			})
			.finally(() => {
				dispatch(showLoader(false))
			})
	}, [orgId, forceUpdate])

	const updateState = (field, key) => (value) => {
		if (field === 'role') {
			value = value.value
		}
		setDetails((state) => ({
			...state,
			[field]: {
				...state[field],
				[key]: value,
			},
		}))
	}

	const disableSubmit = useMemo(() => {
		if (showForm) {
			return true
		} else if (!selectedUser || !details?.designation?.value) {
			return true
		}
		return false
	}, [orgId, details, showForm, selectedUser])

	const disableFormAdd = useMemo(() => {
		if (!details?.designation?.value || !details?.full_name?.value || !details?.mobile_number?.value) {
			return true
		}
		return false
	}, [details])

	const submitUSerForm = () => {
		if (!orgId) {
			dispatch(showToast(true, strings('organisation_id', 'missing'), { type: TOAST_TYPE.ERROR }))
			return
		}
		const obj = {
			name: details?.full_name?.value,
			mobile: details?.mobile_number?.value,
			designation: details?.designation?.value,
			email: details?.email_id?.value,
			organisationId: orgId,
			role: details?.role?.value,
			userId: loggedInUser?.user_id,
		}
		dispatch(showLoader(true))
		addUser(obj)
			.then((response) => {
				dispatch(showToast(true, 'Successfully added user', { type: TOAST_TYPE.SUCCESS }))
			})
			.catch((error) => {
				let error_msg = error?.response?.message ?? 'Error while adding user'
				dispatch(showToast(true, error_msg, { type: TOAST_TYPE.ERROR }))
			})
			.finally(() => {
				update()
				setShowForm(false)
				dispatch(showLoader(false))
			})
	}

	const submitHandler = () => {
		let obj = {
			name: selectedUser?.full_name,
			mobile_number: selectedUser?.mobile_number,
			designation: details?.designation?.value,
			email: selectedUser?.email_id,
		}
		addCallback(obj)
	}

	const addNewUserForm = () => {
		return (
			<InputWrapper>
				<FieldWrapper>
					<Input
						label={strings('name')}
						id='user-name'
						type='text'
						value={details?.full_name?.value}
						onChange={updateState('full_name', 'value')}
						setValidity={updateState('full_name', 'valid')}
						placeholder={strings('your_full_name')}
						errorMap={{
							valueMissing: `${strings('name_required')}`,
						}}
					/>
				</FieldWrapper>
				<BiFieldWrapper>
					<Select
						id='login-country-code'
						data={CountryCodes}
						displayKey='dial_code'
						primaryKey='name'
						value={countryCode}
						onChange={setCountryCode}
						disabled={false}
						type='tel'
						noDropIcon
					/>
					<Input
						label={strings('mobile')}
						id='user-mobile'
						type='tel'
						pattern='^\d{7}\d+$'
						maxLength={10}
						errorMap={{
							patternMismatch: `${strings('enter_valid_num')}`,
							valueMissing: `${strings('mob_num_required')}`,
						}}
						value={details?.mobile_number?.value}
						onChange={updateState('mobile_number', 'value')}
						setValidity={updateState('mobile_number', 'valid')}
						placeholder={strings('enter_your_mobile_num')}
					/>
				</BiFieldWrapper>
				<FieldWrapper>
					<Input
						label={strings('email')}
						id='user-email'
						type='email'
						pattern='^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$'
						value={details?.email_id?.value}
						onChange={updateState('email_id', 'value')}
						setValidity={updateState('email_id', 'valid')}
						errorMap={{
							patternMismatch: `${strings('enter_valid_email')}`,
						}}
						placeholder={strings('your_email_address')}
					/>
				</FieldWrapper>
				<FieldWrapper>
					<Select
						id='select-designation'
						label={strings('designation')}
						placeholder={strings('select', 'designation')}
						data={designationTypes ?? []}
						value={designationTypes.find((item) => item.key === details?.designation?.value) ?? {}}
						disabled={details?.designation?.disabled}
						primaryKey='key'
						displayKey='label'
						required={details?.designation?.required}
						onChange={(val) => updateState('designation', 'value')(val.key)}
					/>
				</FieldWrapper>
				<DrawerButtonsWrapper onRight>
					<Button xs variant='destructive' type='secondary' onClick={() => setShowForm(false)}>
						{strings('cancel')}
					</Button>
					<Button xs onClick={submitUSerForm} disabled={disableFormAdd}>
						{strings('add', 'user')}
					</Button>
				</DrawerButtonsWrapper>
			</InputWrapper>
		)
	}

	return (
		<>
			<DrawerBodyWrapper>
				{showForm ? (
					addNewUserForm()
				) : (
					<>
						<Select
							id='select-user'
							label={strings('user')}
							placeholder={strings('select', 'user')}
							data={userList ?? []}
							value={userList?.find((item) => item.user_id === selectedUser?.user_id) ?? {}}
							primaryKey='user_id'
							displayKey='full_name'
							onChange={(value) => setSelectedUser(value)}
							disabled={false}
						/>
						<Select
							id='select-designation'
							label={strings('designation')}
							placeholder={strings('select', 'designation')}
							data={designationTypes ?? []}
							value={designationTypes.find((item) => item.key === details?.designation?.value) ?? {}}
							disabled={details?.designation?.disabled}
							primaryKey='key'
							displayKey='label'
							required={details?.designation?.required}
							onChange={(val) => updateState('designation', 'value')(val.key)}
						/>
					</>
				)}
			</DrawerBodyWrapper>
			<NoteWrapper>{`Please fulfill finance, management and procurement designations.`}</NoteWrapper>
			<DrawerFooterWrapper>
				<DrawerButtonsWrapper>
					<Button type='secondary' small disabled={showForm} onClick={() => setShowForm(true)}>
						{strings(['add', 'new', 'user'])}
					</Button>
					<Button small disabled={disableSubmit} onClick={submitHandler}>
						{strings('submit')}
					</Button>
				</DrawerButtonsWrapper>
			</DrawerFooterWrapper>
		</>
	)
}

export default AddUserInspection
